/**
 * Regelversion 3: Gesellschaft und Werte.
 *
 * Neue Meinungsthemen mit Stimmung in der Bevölkerung, Positionen und Gewichten der Parteien und
 * der Neigung der Wählergruppen (für die Polarisierung). Die Daten stehen immer bereit, gerechnet
 * wird mit ihnen nur in Partien ab Version 3 (siehe engine/axes.ts).
 *
 * Die Parteien sind fiktiv. Jedes Thema ist eine echte Streitfrage mit nachvollziehbaren Argumenten
 * auf beiden Seiten; das Spiel bewertet keine Seite als richtig.
 */
import { AXN, QS, EQW } from './axes';
import { PUB0 } from './economy';
import { PARTIES } from './parties';

export const AX3: string[] = ['vielfalt', 'queer', 'gendern', 'abtreibung', 'sterbehilfe', 'einbuergerung', 'wehrpflicht', 'wahl16', 'parteiverbot', 'socialmedia', 'rundfunk', 'buergergeld', 'heizung', 'ukraine', 'hassrede'];

Object.assign(AXN, {
  vielfalt: 'Antidiskriminierung', queer: 'Queere Rechte', gendern: 'Genderverbot', abtreibung: 'Schwangerschaftsabbruch',
  sterbehilfe: 'Sterbehilfe', einbuergerung: 'Einbürgerung', wehrpflicht: 'Bedarfswehrpflicht', wahl16: 'Wahlalter 16',
  parteiverbot: 'Parteiverbot', socialmedia: 'Social Media ab 16', rundfunk: 'Rundfunkbeitrag', buergergeld: 'Grundsicherung streichen',
  heizung: '65-%-Heizungspflicht', ukraine: 'Ukraine-Hilfe', hassrede: 'Hass im Netz',
});
Object.assign(QS, {
  vielfalt: 'Der Staat soll Diskriminierung wegen Herkunft, Religion oder Geschlecht stärker bekämpfen, auch mit neuen Pflichten für Behörden und Unternehmen.',
  queer: 'Die Rechte queerer Menschen sollen gestärkt und im Grundgesetz ausdrücklich geschützt werden.',
  gendern: 'Behörden und Schulen sollen keine Gendersprache wie Sternchen oder Doppelpunkt verwenden dürfen.',
  abtreibung: 'Schwangerschaftsabbrüche in den ersten zwölf Wochen sollen nicht mehr im Strafgesetzbuch stehen.',
  sterbehilfe: 'Schwerkranke sollen leichter Hilfe beim Suizid bekommen können.',
  einbuergerung: 'Einbürgerung soll wieder schneller als nach fünf Jahren möglich sein, auch mit doppelter Staatsbürgerschaft.',
  wehrpflicht: 'Melden sich zu wenige Freiwillige für den neuen Wehrdienst, soll die Wehrpflicht aktiviert werden.',
  wahl16: 'Jugendliche sollen ab 16 bei Bundestagswahlen wählen dürfen.',
  parteiverbot: 'Gegen als extremistisch eingestufte Parteien soll ein Verbotsverfahren beantragt werden.',
  socialmedia: 'Soziale Netzwerke sollen für Kinder unter 16 Jahren verboten sein.',
  rundfunk: 'Der Rundfunkbeitrag soll sinken, und die öffentlich-rechtlichen Sender sollen kleiner werden.',
  buergergeld: 'Wer zumutbare Arbeit beharrlich verweigert, soll die Grundsicherung komplett verlieren können.',
  heizung: 'Die Pflicht, dass neue Heizungen zu 65 Prozent mit erneuerbarer Energie laufen, soll wieder eingeführt werden.',
  ukraine: 'Deutschland soll die Ukraine weiter mit Waffen und Geld unterstützen.',
  hassrede: 'Hass und Hetze im Netz sollen härter verfolgt werden, auch wenn Kritiker um die Meinungsfreiheit fürchten.',
});

/** Gewicht der neuen Themen für die eigene Partei (etwas geringer als Wirtschafts- und Sozialfragen). */
export const W3 = .6;
AX3.forEach((a) => { EQW[a] = W3; });

/** Stimmung in der Bevölkerung zu Beginn (−2 klar dagegen … +2 klar dafür). */
// Startwerte nach Umfragen 2025/26 (ARD-DeutschlandTrend, ZDF-Politbarometer, Forsa, Infratest u. a.); unsichere Werte eher vorsichtig gesetzt
Object.assign(PUB0, { vielfalt: .5, queer: .5, gendern: 1.2, abtreibung: 1, sterbehilfe: 1.2, einbuergerung: -1, wehrpflicht: 1, wahl16: -1, parteiverbot: 0, socialmedia: 1, rundfunk: 1, buergergeld: 1.5, heizung: -1, ukraine: 0, hassrede: .6 });

{
  // Positionen abgeleitet aus den Wahlprogrammen 2025 und dem Abstimmungsverhalten bis Oktober 2026 (redaktionelle Einschätzung)
  const NP: Record<string, Record<string, number>> = {
    PdA: { vielfalt: 1.5, queer: 1.5, gendern: -1, abtreibung: 2, sterbehilfe: 1, einbuergerung: 0, wehrpflicht: 0, wahl16: 2, parteiverbot: 1, socialmedia: 1, rundfunk: -1, buergergeld: 1, heizung: -1, ukraine: 1, hassrede: 1.5 },
    BU: { vielfalt: -.5, queer: -.5, gendern: 1, abtreibung: -2, sterbehilfe: -1, einbuergerung: -2, wehrpflicht: 2, wahl16: -2, parteiverbot: -1, socialmedia: 1, rundfunk: .5, buergergeld: 2, heizung: -2, ukraine: 2, hassrede: .5 },
    LR: { vielfalt: 0, queer: 1.5, gendern: -1, abtreibung: 0, sterbehilfe: 2, einbuergerung: -1, wehrpflicht: -1, wahl16: 2, parteiverbot: -1, socialmedia: -1, rundfunk: 1, buergergeld: 2, heizung: -2, ukraine: 2, hassrede: -1 },
    OA: { vielfalt: 2, queer: 2, gendern: -2, abtreibung: 2, sterbehilfe: 1, einbuergerung: 2, wehrpflicht: -1, wahl16: 2, parteiverbot: 2, socialmedia: 0, rundfunk: -2, buergergeld: -1, heizung: 2, ukraine: 2, hassrede: 1.5 },
    LB: { vielfalt: 2, queer: 2, gendern: -2, abtreibung: 2, sterbehilfe: 1, einbuergerung: 2, wehrpflicht: -2, wahl16: 2, parteiverbot: 2, socialmedia: 0, rundfunk: -1.5, buergergeld: -2, heizung: 1, ukraine: -1, hassrede: 1 },
    HO: { vielfalt: -2, queer: -2, gendern: 2, abtreibung: -2, sterbehilfe: -1, einbuergerung: -2, wehrpflicht: 2, wahl16: -2, parteiverbot: -2, socialmedia: -1, rundfunk: 2, buergergeld: 2, heizung: -2, ukraine: -2, hassrede: -2 },
  };
  const NW: Record<string, Record<string, number>> = {
    PdA: { buergergeld: 1.2, abtreibung: .8 }, BU: { buergergeld: 1.3, wehrpflicht: 1.1, gendern: .8 }, LR: { rundfunk: 1, buergergeld: 1.1, sterbehilfe: .9, heizung: 1 },
    OA: { heizung: 1.6, queer: 1.1, vielfalt: 1.1, ukraine: 1 }, LB: { buergergeld: 1.6, ukraine: 1.2, vielfalt: 1 }, HO: { einbuergerung: 1.8, gendern: 1.3, rundfunk: 1.3, parteiverbot: 1.8, heizung: 1.3, ukraine: 1.2 },
  };
  for (const k in NP) { Object.assign(PARTIES[k].pos, NP[k]); AX3.forEach((a) => { PARTIES[k].w[a] = (NW[k] || {})[a] ?? W3; }); }
}

/**
 * Neigung der Wählergruppen zur Aussage eines Themas (−1 dagegen … +1 dafür).
 * Grundlage der Polarisierung: Gesetze und Entscheidungen zu diesen Themen bewegen die Gruppen gegeneinander.
 */
export const LEAN: Record<string, Partial<Record<'rent' | 'fam' | 'arb' | 'unt' | 'jung', number>>> = {
  vielfalt: { jung: .8, rent: -.4, fam: .1, unt: .1 },
  queer: { jung: 1, rent: -.5, arb: -.1, unt: .2 },
  gendern: { rent: .8, arb: .5, fam: .2, unt: .2, jung: -.6 },
  abtreibung: { jung: .8, fam: .3, arb: .2, rent: -.4 },
  sterbehilfe: { rent: .4, jung: .3 },
  einbuergerung: { jung: .5, unt: .5, arb: -.3, rent: -.6 },
  wehrpflicht: { rent: .8, unt: .2, arb: .1, fam: -.3, jung: -1 },
  wahl16: { jung: 1, fam: .1, rent: -.6 },
  parteiverbot: { jung: .4, arb: -.2 },
  socialmedia: { fam: 1, rent: .6, unt: -.3, jung: -.8 },
  rundfunk: { arb: .4, unt: .5, jung: .3, rent: -.6 },
  buergergeld: { unt: .8, rent: .6, arb: .6, jung: -.2 },
  heizung: { jung: .6, arb: -.3, unt: -.3, fam: -.5, rent: -.8 },
  ukraine: { unt: .3, jung: .2, arb: -.2 },
  hassrede: { jung: .3, rent: .2 },
};

/** Kulturkampf-Themen: Hier polarisieren klare Positionen besonders. */
export const POLAR = new Set(['vielfalt', 'queer', 'gendern', 'abtreibung', 'einbuergerung', 'wehrpflicht', 'wahl16', 'parteiverbot', 'rundfunk', 'buergergeld', 'heizung', 'ukraine', 'hassrede']);
